import { state, type Screen } from "./state";
import { runOpportunisticChecks } from "./missionChecks";
import { runHiredShipUpkeep } from "../shipUpkeep";
import { runProvisionsTick } from "../provisions";
import { runMutinyCheck } from "../mutiny";
import { bootAuth, installUnloadSave, triggerSave } from "./persistence";
import { pauseClock, resumeClock } from "../gameClock";
import { renderLoginScreen } from "./screens/loginScreen";
import { renderShipSelectScreen } from "./screens/shipSelectScreen";
import { renderWelcomeScreen } from "./screens/welcomeScreen";
import { renderFaqScreen } from "./screens/faqScreen";
import { renderMarketOverviewScreen } from "./screens/marketOverviewScreen";
import { renderMissionBoardScreen } from "./screens/missionBoardScreen";
import { renderMissionDiaryScreen } from "./screens/missionDiaryScreen";
import { renderExpeditionScreen } from "./screens/expeditionScreen";
import { renderSpaceMapScreen } from "./screens/spaceMapScreen";
import { renderCargoScreen } from "./screens/cargoScreen";
import { renderShipyardScreen } from "./screens/shipyardScreen";
import { renderFleetScreen } from "./screens/fleetScreen";
import { renderMarketScreen } from "./screens/marketScreen";
import { renderLotViewScreen } from "./screens/lotViewScreen";
import { renderMainMenuScreen } from "./screens/mainMenuScreen";
import { renderOptionsScreen } from "./screens/optionsScreen";
import { renderShipCardScreen } from "./screens/shipCardScreen";
import { renderEscapePodScreen } from "./screens/escapePodScreen";
import { renderSaveCompareScreen } from "./screens/saveCompareScreen";
import { findBaseAt } from "../baseStorage";
import { renderCaptainsLoungeScreen } from "./screens/captainsLoungeScreen";
import { renderTrophyRoomScreen } from "./screens/trophyRoomScreen";
import { renderDevNavBar } from "./components";
import { resumeAudio } from "../audio";
import { installResponsiveStage } from "./responsiveStage";
import { paneLayoutFor } from "./paneLayout";

export interface ScreenContext {
  rerender: () => void;
  navigate: (screen: Screen) => void;
  // Full resync -- tears down and rebuilds even the normally-persistent
  // Expedition View before re-rendering. Needed only where local state was
  // replaced wholesale rather than edited in place: the "server wins"
  // conflict path (persistence.ts's doSave). An ordinary rerender()
  // deliberately leaves a mounted Expedition View alone (that's the whole
  // point of the persistent containers below), which would otherwise leave
  // it flying the OLD System/Location scene while state says something
  // entirely different.
  resync: () => void;
}

// The three real top-level "camera" Views (Section 2a) -- the only
// screens the captain is ever actually *in*. Everything else is either
// the Main Menu flow or a pane drawn on top of one of these.
type TopLevelView = "expedition" | "base" | "station";
const TOP_LEVEL_VIEWS = new Set<Screen>(["expedition", "base", "station"]);

// The Main Menu flow -- deliberately outside the View/pane model (Don:
// "Menu is the fourth view, we'll not touch that for now").
const MENU_SCREENS = new Set<Screen>(["checking", "mainMenu", "login"]);

// Every other UI is a PANE: it renders on top of whichever top-level View
// is currently active, which stays mounted and visible underneath (Don's
// direction: "review our other UIs, look at which ones currently are
// displayed as a full screen that hides these three views -- update those
// so they are simply panes"). For the Expedition View specifically this
// also means it keeps *running* underneath -- real-time combat/mining and
// the game clock never stop just because a pane is open (16.36).
//
// Ship Select is the one screen deliberately left out: it's reached only
// before a ship exists (or via DEV DEBUG), and every top-level View bails
// to it when currentShip is null, so rendering a View underneath it would
// bounce straight back here.
const PANE_SCREENS = new Set<Screen>([
  "cargo",
  "fleet",
  "market",
  "board",
  "diary",
  "spaceMap",
  "shipyard",
  "lounge",
  "trophies",
  "options",
  "shipCard",
  // The "?" handbook opens over whichever View is active, same as every
  // other reference pane -- a captain looking something up mid-flight should
  // not have space torn down behind them.
  "faq",
  "marketOverview",
  "saveCompare"
]);

export function mountApp(root: HTMLElement): void {
  // Browser autoplay policy blocks an AudioContext created outside a user
  // gesture (16.69) -- one capture-phase listener on the first real
  // interaction of any kind unlocks audio for the rest of the session,
  // rather than every individual button having to know about it.
  const unlockAudio = () => resumeAudio();
  root.addEventListener("pointerdown", unlockAudio, { once: true, capture: true });
  root.addEventListener("keydown", unlockAudio, { once: true, capture: true });

  const ctx: ScreenContext = {
    rerender: () => render(),
    navigate: (screen) => {
      state.screen = screen;
      render();
    },
    resync: () => {
      // Force the Expedition View to rebuild from the new state on the
      // next render(), instead of being left mounted with a stale scene.
      if (expeditionMounted) {
        expeditionContainer.innerHTML = "";
        expeditionMounted = false;
      }
      render();
    }
  };

  // Four containers, created once and never removed from `root` for the
  // app's lifetime -- only their contents/visibility change per render().
  // This is what lets the Expedition View's own DOM (and its
  // requestAnimationFrame loop, which stops the instant its wrapper
  // element is disconnected -- see expeditionScreen.ts's frame()) survive
  // an overlay opening on top of it, instead of being rebuilt from
  // scratch on every navigation the way every other screen already is.
  const devBarContainer = document.createElement("div");
  const mainContainer = document.createElement("div");
  // .app-main: inside the locked tablet stage (16.83) #app becomes the
  // 1280x800 stage itself, so the 900px reading column it used to provide
  // moves onto this container. Unused on desktop.
  mainContainer.className = "app-main";
  const expeditionContainer = document.createElement("div");
  const overlayContainer = document.createElement("div");
  overlayContainer.className = "overlay-screen-backdrop";
  root.append(devBarContainer, mainContainer, expeditionContainer, overlayContainer);

  // Lock the layout to one design stage on touch devices; leave desktop fluid
  // (16.83). Installed once, before the first render, so the very first screen
  // is already laid out at stage dimensions rather than reflowing a frame in.
  installResponsiveStage(window);

  // Whether the Expedition View is actually alive in expeditionContainer
  // right now -- distinct from state.screen, since state.screen moves to
  // "cargo"/"fleet"/etc. while an overlay is open but the Expedition View
  // underneath must stay mounted and ticking regardless.
  let expeditionMounted = false;

  function renderMainScreen(container: HTMLElement, screen: Screen): void {
    if (screen === "checking" || screen === "mainMenu") {
      renderMainMenuScreen(container, ctx);
    } else if (screen === "login") {
      renderLoginScreen(container, ctx);
    } else if (screen === "welcome") {
      renderWelcomeScreen(container, ctx);
    } else if (screen === "faq") {
      renderFaqScreen(container, ctx);
    } else if (screen === "marketOverview") {
      renderMarketOverviewScreen(container, ctx);
    } else if (screen === "ships") {
      renderShipSelectScreen(container, ctx);
    } else if (screen === "board") {
      renderMissionBoardScreen(container, ctx);
    } else if (screen === "spaceMap") {
      renderSpaceMapScreen(container, ctx);
    } else if (screen === "cargo") {
      renderCargoScreen(container, ctx);
    } else if (screen === "shipyard") {
      renderShipyardScreen(container, ctx);
    } else if (screen === "fleet") {
      renderFleetScreen(container, ctx);
    } else if (screen === "lounge") {
      renderCaptainsLoungeScreen(container, ctx);
    } else if (screen === "trophies") {
      renderTrophyRoomScreen(container, ctx);
    } else if (screen === "market") {
      renderMarketScreen(container, ctx);
    } else if (screen === "base") {
      renderLotViewScreen(container, ctx, "base");
    } else if (screen === "station") {
      renderLotViewScreen(container, ctx, "station");
    } else if (screen === "options") {
      renderOptionsScreen(container, ctx);
    } else if (screen === "shipCard") {
      renderShipCardScreen(container, ctx);
    } else if (screen === "escapePod") {
      renderEscapePodScreen(container, ctx);
    } else if (screen === "saveCompare") {
      renderSaveCompareScreen(container, ctx);
    } else {
      renderMissionDiaryScreen(container, ctx);
    }
  }

  function render(): void {
    // A brand-new captain reads the welcome + tutorial before choosing a hull
    // (Don's direction). Enforced here, before dispatch, rather than at each
    // place that navigates to Ship Select -- there are four such routes today
    // (New Game, logout, session adoption, and the no-ship fallback) and the
    // guarantee is "a new captain never reaches Ship Select unbriefed", which
    // a rule at the destination keeps and four call sites would eventually
    // not. Same reasoning as the Base View guard directly below.
    if (state.screen === "ships" && !state.hasSeenWelcome) {
      state.screen = "welcome";
    }

    // Base View requires actually being at a Base you own (16.76).
    // Previously it was openable from the nav bar anywhere, which showed
    // a "your base" screen at planets never leased -- and worse, every
    // lot read as already built, because lotViewScreen's own builtAt()
    // treats a missing base as ungated. Rewritten here, before dispatch,
    // so every route into the screen is covered rather than just the nav
    // button.
    if (
      state.screen === "base" &&
      !findBaseAt(state.bases, state.currentSystemId, state.currentLocationId)
    ) {
      state.lastEvent = "No Base here — buy a lease and dock at a planet you own.";
      state.screen = state.currentShip ? "expedition" : "ships";
    }

    // Real game clock (16.34/16.36, src/gameClock.ts) -- pauses while
    // logged out and while docked (Base/Station View), runs continuously
    // otherwise, including the whole time spent in the Expedition View
    // and any overlay opened on top of it (those never change away from
    // being "in the Expedition View" for clock purposes). Checked fresh
    // on every render, the same pattern already used for hop-distance and
    // mission completion below -- no separate polling loop.
    // Local-first (16.57): an unauthenticated captain playing entirely
    // offline via localStorage is a real, live gameplay session once past
    // the Main Menu/Login screens -- pausing the clock just because
    // they're not logged in would silently break every clock-driven
    // system (Provisions, hired-ship upkeep, VIP deadlines) for offline
    // play, which contradicts CLAUDE.md's "fully playable via localStorage
    // alone." Screen context alone (MENU_SCREENS/Base/Station) decides the
    // pause now, not auth status.
    const shouldPauseClock =
      MENU_SCREENS.has(state.screen) || // 16.34: "logging out to the main menu... pauses time"
      state.screen === "base" ||
      state.screen === "station";
    state.clock = shouldPauseClock ? pauseClock(state.clock, Date.now()) : resumeClock(state.clock, Date.now());

    // Opportunistic completion checks (16.34): recomputed fresh on every
    // render, anywhere in the app -- not a dedicated polling loop, the
    // same pattern already used for hop-distance. Only save if something
    // actually changed. Skipped pre-login/pre-boot, when there's no
    // authenticated game state to check or save yet.
    if (state.screen !== "checking" && state.screen !== "mainMenu" && state.screen !== "login") {
      const missionsChanged = runOpportunisticChecks(state);
      const upkeepChanged = runHiredShipUpkeep(state, Date.now());
      const provisionsChanged = runProvisionsTick(state, Date.now());
      const mutinyChanged = runMutinyCheck(state, Date.now());
      if (missionsChanged || upkeepChanged || provisionsChanged || mutinyChanged) {
        triggerSave(ctx);
      }
      if (mutinyChanged && state.inEscapePod && state.screen !== "escapePod") {
        ctx.navigate("escapePod");
      }
    }

    devBarContainer.innerHTML = "";
    if (state.screen !== "checking" && state.screen !== "mainMenu" && state.screen !== "login") {
      renderDevNavBar(devBarContainer, ctx);
    }

    // Which top-level View sits underneath right now: the screen itself if
    // it IS one, otherwise whichever View the pane was opened from
    // (lastTopLevelView, kept current by the Views themselves). null while
    // in the Main Menu flow, or on Ship Select before a ship exists --
    // both render standalone with no View behind them.
    const isMenu = MENU_SCREENS.has(state.screen);
    const isPane = PANE_SCREENS.has(state.screen) && !!state.currentShip;
    const underlyingView: TopLevelView | null = isMenu
      ? null
      : TOP_LEVEL_VIEWS.has(state.screen)
        ? (state.screen as TopLevelView)
        : isPane
          ? (state.lastTopLevelView ?? "expedition")
          : null;

    if (underlyingView === "expedition") {
      if (!expeditionMounted) {
        expeditionContainer.innerHTML = "";
        renderExpeditionScreen(expeditionContainer, ctx);
        expeditionMounted = true;
      }
      expeditionContainer.style.display = "";
    } else if (expeditionMounted) {
      // A real "leave space" navigation (docking, or back to the Menu) --
      // genuinely tears down the Expedition View, stopping its frame()
      // loop by disconnecting wrap.
      expeditionContainer.innerHTML = "";
      expeditionMounted = false;
    }

    overlayContainer.innerHTML = "";
    if (isPane) {
      // Closing a pane returns to the View it was opened over -- not
      // always the Expedition View, now that panes also open over Base
      // and Station View.
      const closePane = (): void => {
        state.screen = underlyingView ?? "expedition";
        render();
      };
      overlayContainer.style.display = "flex";
      // Over the Expedition View the pane is inset to the gap between its
      // HUD chrome (see the CSS), because the captain must be able to see that
      // space carries on around them -- nothing out there is paused.
      overlayContainer.classList.toggle("over-expedition", underlyingView === "expedition");
      // At a dock the opposite is true (16.102, Don's direction): "in a Station
      // or Base when we open a module we aren't worried about what's happening
      // elsewhere in the base." So the module fills the screen.
      //
      // This also fixes a real defect rather than only a preference. Every pane
      // renders its own header (ship, location, credits, waypoint) while the Lot
      // View underneath kept rendering the same header -- so the captain saw the
      // information twice, and the copy behind was STALE, because only the pane
      // re-renders as credits change. One screen, one header, one truth.
      const paneLayout = paneLayoutFor(underlyingView);
      overlayContainer.classList.toggle("full-screen-pane", paneLayout.fullScreen);
      // Assignment (not addEventListener) so repeated renders replace
      // this handler instead of stacking a new one on the same
      // never-removed overlayContainer every time.
      overlayContainer.onclick = (e) => {
        if (e.target === overlayContainer) {
          closePane();
        }
      };
      const panel = document.createElement("div");
      panel.className = "overlay-screen-panel";
      const closeBtn = document.createElement("button");
      closeBtn.type = "button";
      closeBtn.className = "overlay-screen-close";
      // A bare glyph is enough on an inset pane, where the View is visible all
      // around it. On a full screen it is the only way out and nothing around it
      // says where "out" goes, so it says so itself (16.102).
      closeBtn.textContent = paneLayout.closeLabel;
      closeBtn.addEventListener("click", closePane);
      panel.appendChild(closeBtn);
      overlayContainer.appendChild(panel);
      renderMainScreen(panel, state.screen);
    } else {
      overlayContainer.style.display = "none";
      overlayContainer.onclick = null;
    }

    // mainContainer carries whatever isn't the Expedition View: the Menu
    // flow, Ship Select, and Base/Station View -- including when one of
    // those two is sitting underneath an open pane.
    mainContainer.innerHTML = "";
    const mainScreen: Screen | null =
      underlyingView === "base" || underlyingView === "station"
        ? // 16.102: not rendered at all while a module is open over it. Hiding it
          // by covering it up would leave a second, stale header alive in the
          // DOM one z-index below -- the thing Don actually reported. The Lot
          // View is rebuilt when the module closes, so it comes back current.
          isPane
          ? null
          : underlyingView
        : underlyingView === "expedition"
          ? null
          : state.screen;

    if (mainScreen) {
      mainContainer.style.display = "";
      renderMainScreen(mainContainer, mainScreen);
    } else {
      mainContainer.style.display = "none";
    }
  }

  render();
  // 16.99: a fight in progress must survive a closed tab, and there is no
  // periodic autosave to fall back on.
  installUnloadSave();
  bootAuth().then(() => {
    // A verification or password-reset link (design doc 16.90) lands on the
    // game's own URL with a token in the query string, because the token is
    // redeemed by a POST from the client rather than by fetching a link. So
    // the token has to route: without this the app boots to the Main Menu,
    // the login screen that reads the token never renders, and the link
    // silently does nothing -- which is exactly what happened the first time
    // a real link was clicked.
    //
    // Checked after bootAuth so an already-signed-in player is not bounced to
    // a login screen; a stale token in the URL is then simply ignored.
    const params = new URLSearchParams(window.location.search);
    if (state.authStatus !== "authenticated" && (params.has("verify") || params.has("reset"))) {
      state.screen = "login";
    }
    render();
  });

  // Best-effort only (Section 16.34: "closing the tab/browser pauses
  // time") -- there's no reliable way to guarantee an async save
  // completes before the page actually unloads, so this just gives the
  // in-flight save a chance to include the pause rather than silently
  // never trying. The authoritative pause paths (logout, docking) don't
  // depend on this firing.
  window.addEventListener("beforeunload", () => {
    if (state.authStatus === "authenticated") {
      state.clock = pauseClock(state.clock, Date.now());
      triggerSave(ctx);
    }
  });
}
